/**
 * Spawn-rule variants for the round-2 overworld hostiles. The generic spawner
 * (entities/spawning.ts) picks a zombie / skeleton / ... first, finds a floor
 * and then asks `extraHostile` whether this spot calls for something else:
 *  - slime: in slime chunks below y 40 (any light) and in swamps (51..69, dark)
 *  - husk: replaces zombies in deserts (open sky)
 *  - stray: replaces skeletons in cold biomes (open sky)
 * Drowned (water spawns), cave spiders (mineshaft spawners) and silverfish
 * (infested blocks / strongholds) have no natural spawn yet.
 */

import type { MobType } from '../../game/types';
import { isSlimeChunk } from './slimeChunk';

export interface ExtraPick { type: MobType; min: number; max: number; anyLight: boolean }

const COLD = /snowy|frozen|ice|grove|jagged|taiga.*snow/;

/**
 * @param base    the type the generic table rolled
 * @param rawSky  raw (unscaled) sky light 0..15 at the spawn cell
 * @param biome   lower-case biome name ('' when unknown)
 */
export function extraHostile(rnd: () => number, base: MobType, x: number, y: number, z: number, rawSky: number, biome: string): ExtraPick | null {
  if (y < 40 && isSlimeChunk(x >> 4, z >> 4) && rnd() < 0.25) return { type: 'slime', min: 1, max: 2, anyLight: true };
  if ((biome.includes('swamp') || biome.includes('mangrove')) && y >= 51 && y <= 69 && rnd() < 0.08) {
    return { type: 'witch', min: 1, max: 1, anyLight: false };
  }
  if ((biome.includes('swamp') || biome.includes('mangrove')) && y >= 51 && y <= 69 && rnd() < 0.3) {
    return { type: 'slime', min: 1, max: 1, anyLight: false };
  }
  if (rawSky >= 15) {
    if (base === 'zombie' && biome.includes('desert') && rnd() < 0.8) return { type: 'husk', min: 1, max: 4, anyLight: false };
    if (base === 'skeleton' && COLD.test(biome) && rnd() < 0.8) return { type: 'stray', min: 1, max: 3, anyLight: false };
  }
  return null;
}

/** Forest / taiga chunks that rolled a herd turn a quarter of them into wolf packs. */
export function herdVariant(roll: number, biome: string): boolean {
  return roll < 0.25 && /forest|taiga|grove/.test(biome) && !biome.includes('flower');
}
